import { useInsightGlobalConfig } from '@jira/servicedesk__insight-global-configuration-store/src/useInsightGlobalConfig.tsx';
import {
	DATA_MANAGER_ADMIN,
	DATA_MANAGER_ADAPTERS_ADMIN,
	DATA_MANAGER_REPORT_VIEWER,
	DATA_MANAGER_REPORT_EDITOR,
	DATA_MANAGER_OPEN_ROUTE_ALL_ROLES,
	DATA_MANAGER_CLASS_SETTING_ROUTE_NAME,
} from '@jira/servicedesk__insight-common-types-shared-types/src/TypesDATA_MANAGER_ADMIN.types.tsx';
import { useAssetsIsSiteAdmin } from '@jira/assets-app__assets-tenant-context-provider/src/useAssetsTenantContext.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createRouterSelector } from '@atlassian/react-resource-router';
import type { RouteName } from '@jira/platform__routing-route-name-types/src/RouteName.tsx';

// oxlint-disable-next-line typescript/no-invalid-void-type
export const useUrl: (args_0: void) => string = createRouterSelector(({ match }) => match.url);

const isDataManagerAdmin = (roles?: string[]) =>
	roles?.includes(DATA_MANAGER_ADMIN) || roles?.includes(DATA_MANAGER_ADAPTERS_ADMIN);

// oxlint-disable-next-line typescript/no-invalid-void-type
export const useRouteName: (args_0: void) => RouteName = createRouterSelector(
	(state) => state.route.name,
);

export const useAssetsDataManagerUserAuth = (): {
	isAssetsDataManagerUnauthorizedUser: boolean;
} => {
	const url = useUrl();
	const isSiteAdmin = useAssetsIsSiteAdmin();
	const [{ dataManager, loading: isInsightGlobalConfigLoading }] = useInsightGlobalConfig();
	const {
		isProvisioned: isDataManagerProvisioned,
		provisioningLoading,
		myRolesLoading,
	} = dataManager ?? {};
	const isLoading = isInsightGlobalConfigLoading || myRolesLoading || provisioningLoading;
	const routeName = useRouteName();
	const { isAdmin, isDashboardEditor } = useAssetsDataManagerAccess();

	if (
		!url.startsWith('/jira/assets/data-manager') ||
		isLoading ||
		!fg('assets_data_manager_jira_spa')
	) {
		return { isAssetsDataManagerUnauthorizedUser: false };
	}

	if (!isDataManagerProvisioned) {
		return { isAssetsDataManagerUnauthorizedUser: true };
	}

	let isAuthorized = isSiteAdmin || isAdmin;

	if (isAuthorized) {
		return { isAssetsDataManagerUnauthorizedUser: false };
	}

	// For all open routes, display the page regardless of the role,
	// except for object class settings route because, certain nested routes in it have access for all roles.
	if (DATA_MANAGER_OPEN_ROUTE_ALL_ROLES.includes(routeName)) {
		const isObjectClassSettingsRouteWithAccessForAllRoles =
			routeName === 'assets-data-manager-object-class-settings' &&
			!url.includes('mapping-matrix') &&
			!url.includes('cleansing-statistics');
		isAuthorized = !isObjectClassSettingsRouteWithAccessForAllRoles;
	}

	// If the user is a dashboard editor and
	// the route is the data dictionaries route, allow access.
	if (
		isDashboardEditor &&
		routeName === DATA_MANAGER_CLASS_SETTING_ROUTE_NAME &&
		url.includes('data-dictionaries')
	) {
		isAuthorized = true;
	}

	return {
		isAssetsDataManagerUnauthorizedUser: isAuthorized === false,
	};
};

export const useAssetsDataManagerAccess = (): {
	isAdmin: boolean;
	isDashboardEditor: boolean;
	isDashboardViewer: boolean;
} => {
	const [
		{
			dataManager: { myRoles },
		},
	] = useInsightGlobalConfig();
	let isAdmin = false;
	let isDashboardEditor = false;
	let isDashboardViewer = false;

	if (isDataManagerAdmin(myRoles)) {
		isAdmin = true;
		isDashboardEditor = true;
		isDashboardViewer = true;
	} else if (myRoles?.includes(DATA_MANAGER_REPORT_EDITOR)) {
		isDashboardEditor = true;
		isDashboardViewer = true;
	} else if (myRoles?.includes(DATA_MANAGER_REPORT_VIEWER)) {
		isDashboardViewer = true;
	}

	return {
		isAdmin,
		isDashboardEditor,
		isDashboardViewer,
	};
};
